'use client';

import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, TextField, BlockStack, FormLayout, InlineStack } from '@shopify/polaris';
import * as MediaAssetAPI from '@/api/media-asset';
import {
  useCompletedMediaAsset,
  useMediaAssetPollingContext,
} from '@/features/ai-video-creator/components/MediaAssetPollingContext';
import { useCurrentApp } from '@/stores/userDataStore';
import MediaAssetCard from '../media-asset/MediaAssetCard';
import {
  AssetCreationToolName,
  CompletedStandaloneMediaAsset,
  GenerationInstrument,
  MediaAssetStatus,
} from '@/types/media';
import { useInsufficientBalance } from '@/features/billing/components/InsufficientBalanceContext';
import { PricesService } from '@/features/billing';
import DismissibleBanner from '../DismissibleBanner';
import { SessionBanner } from '@/types/banners';

interface TextToPersonalAvatarImageProps {
  productId?: string;
  productTitle?: string;
  initialPrompt?: string;
  hideUseAction?: boolean;
  onGenerationComplete?: (mediaAsset: CompletedStandaloneMediaAsset) => void;
}

const CREDITS = PricesService.getPriceText(AssetCreationToolName.personalAvatarAiImage, [
  GenerationInstrument.textToImage,
]);
const CREDITS_TEXT = CREDITS ? ` (${CREDITS})` : '';

export default function TextToPersonalAvatarImage({
  initialPrompt = '',
  hideUseAction = false,
  onGenerationComplete,
}: TextToPersonalAvatarImageProps) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const { addMediaAssetToPollingQueue } = useMediaAssetPollingContext();
  const { appKey } = useCurrentApp();
  const { checkBalance } = useInsufficientBalance();

  const generateMutation = useMutation({
    mutationFn: () =>
      MediaAssetAPI.generatePersonalAvatarImage({
        appKey,
        imagePrompt: prompt,
      }),
  });

  const handlePromptChange = (value: string) => {
    setPrompt(value);
  };

  const handleGenerate = () => {
    if (!checkBalance(AssetCreationToolName.personalAvatarAiImage, [GenerationInstrument.imageToImage])) {
      return;
    }
    generateMutation.mutate(undefined, {
      onSuccess: (data) => {
        if (!data) return;
        addMediaAssetToPollingQueue(data);
      },
    });
  };

  const { isLoading, mediaAsset: initialMediaAsset, hasError } = useCompletedMediaAsset(generateMutation.data);
  const mediaAsset = initialMediaAsset as CompletedStandaloneMediaAsset;

  const isGenerating = generateMutation.isPending || isLoading;
  const isError = !!generateMutation.error || hasError;
  useEffect(() => {
    if (mediaAsset?.status === MediaAssetStatus.DONE) {
      onGenerationComplete?.(mediaAsset);
    }
  }, [mediaAsset]);

  return (
    <BlockStack gap="600">
      <FormLayout>
        <TextField
          label="Prompt"
          labelHidden
          value={prompt}
          onChange={handlePromptChange}
          autoComplete="off"
          placeholder="Enter a prompt..."
          disabled={isGenerating}
        />
      </FormLayout>
      <InlineStack align="end">
        <Button
          size="large"
          variant="secondary"
          onClick={handleGenerate}
          disabled={isGenerating || !prompt.trim()}
          loading={isGenerating}
        >
          Generate{CREDITS_TEXT}
        </Button>
      </InlineStack>

      {!isGenerating && !isError && mediaAsset && (
        <InlineStack align="center">
          <MediaAssetCard media={mediaAsset} hideUseAction={hideUseAction} />
        </InlineStack>
      )}

      <DismissibleBanner
        tone="critical"
        title="Generation Failed"
        showBanner={isError}
        sessionBanner={SessionBanner.PersonalAvatarGenerationError}
      >
        Error generating influencer. Please try again or contact support.
      </DismissibleBanner>
    </BlockStack>
  );
}
